/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/ai-rulegen.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * Which stage produced a suggestion, ordered by how much is *known* rather than inferred.
 * `schema_declared` — the source itself says so. A `NOT NULL` column yields a rule that cannot be
 * wrong about the constraint. `profile_observed` — measured by the profiler ("no nulls in 48,210
 * rows", "every value distinct, by exact count"): stronger than a convention, weaker than a
 * declaration, because a profile measures what the data was rather than what it must be.
 * `schema_inferred` — derived from naming or type conventions, still deterministic and weaker,
 * because a convention holds until a customer names things differently. `model_proposed` — a
 * language model's suggestion, never labelled as either of the others, because the label is what
 * tells a reviewer which to read hardest.
 */
export type CandidateSource = 'schema_declared' | 'profile_observed' | 'schema_inferred' | 'model_proposed';

export const CANDIDATE_SOURCES: readonly CandidateSource[] = ['schema_declared', 'profile_observed', 'schema_inferred', 'model_proposed'];

export function isCandidateSource(value: unknown): value is CandidateSource {
  return (['schema_declared', 'profile_observed', 'schema_inferred', 'model_proposed'] as readonly unknown[]).includes(value);
}

/**
 * Why a candidate was refused. Closed, because these are counted: a generator whose output is
 * mostly refused is one this deployment should not run, and that is only answerable if the reasons
 * are a vocabulary. `unknown_column` and `not_visible` are deliberately separate. The first is a
 * model inventing something; the second is a model naming something real that *this person* may
 * not see. One is a model-capability problem and the other is an access-control event.
 */
export type RejectionReason = 'unknown_column' | 'not_visible' | 'malformed' | 'duplicate' | 'unsupported_kind';

export const REJECTION_REASONS: readonly RejectionReason[] = ['unknown_column', 'not_visible', 'malformed', 'duplicate', 'unsupported_kind'];

export function isRejectionReason(value: unknown): value is RejectionReason {
  return (['unknown_column', 'not_visible', 'malformed', 'duplicate', 'unsupported_kind'] as readonly unknown[]).includes(value);
}

/**
 * What happened to stage 2 on one generation run. **Seven states, and none of them is a
 * fabrication.** The requirement this exists for is that a client may never show "AI suggested"
 * when no AI ran, and may never report a stage-1-only run as though a model had examined the
 * schema and found nothing worth saying. A boolean cannot hold those apart, which is why
 * `model_consulted` is *derived* from this rather than tracked beside it. `not_configured` — no
 * proposer is wired in this deployment; the supported default for an air-gapped install.
 * `no_route` — a proposer is wired and this tenant has no model routed, or switched theirs off; a
 * configuration answer, not an outage. `unavailable` — a model was routed and could not be
 * reached; **our** outage. `rejected` — the model answered and the validator refused it, or the
 * capability matrix refused the model; a capability signal. `contributed` — the model answered and
 * the validator accepted it, and it is the **only** state under which any suggestion carries
 * `source: model_proposed`. `nothing_to_ask` — stage 1 covered every column, or the model added
 * nothing beyond it. `deferred` — the run followed a harvest, so the model was deliberately not
 * asked; it is asked when the table is profiled or when somebody generates again.
 */
export type ModelStatus = 'not_configured' | 'no_route' | 'unavailable' | 'rejected' | 'contributed' | 'nothing_to_ask' | 'deferred';

export const MODEL_STATUSES: readonly ModelStatus[] = ['not_configured', 'no_route', 'unavailable', 'rejected', 'contributed', 'nothing_to_ask', 'deferred'];

export function isModelStatus(value: unknown): value is ModelStatus {
  return (['not_configured', 'no_route', 'unavailable', 'rejected', 'contributed', 'nothing_to_ask', 'deferred'] as readonly unknown[]).includes(value);
}

/**
 * What a person did with a recommendation. **There is no `accepted` or `approved`.** `drafted`
 * means a draft rule was authored from it; whether that rule is active is `rule.state`, read live
 * from `core.rules`. `bound` means the activated rule was bound to the dataset. `dismissed`
 * carries the reason.
 */
export type SuggestionStatus = 'pending' | 'drafted' | 'bound' | 'dismissed';

export const SUGGESTION_STATUSES: readonly SuggestionStatus[] = ['pending', 'drafted', 'bound', 'dismissed'];

export function isSuggestionStatus(value: unknown): value is SuggestionStatus {
  return (['pending', 'drafted', 'bound', 'dismissed'] as readonly unknown[]).includes(value);
}

/**
 * Who proposed it, at the granularity a filter needs. `hybrid` is a deterministic recommendation
 * the model independently proposed too — the rule and its rationale stay the deterministic
 * stage's.
 */
export type Origin = 'system' | 'ai' | 'hybrid';

export const ORIGINS: readonly Origin[] = ['system', 'ai', 'hybrid'];

export function isOrigin(value: unknown): value is Origin {
  return (['system', 'ai', 'hybrid'] as readonly unknown[]).includes(value);
}

/**
 * Whether the latest profile informed a run. `stale` means the newest profile is of an older
 * schema version, so nothing was read from it.
 */
export type ProfileStatus = 'used' | 'never_profiled' | 'stale' | 'empty';

export const PROFILE_STATUSES: readonly ProfileStatus[] = ['used', 'never_profiled', 'stale', 'empty'];

export function isProfileStatus(value: unknown): value is ProfileStatus {
  return (['used', 'never_profiled', 'stale', 'empty'] as readonly unknown[]).includes(value);
}

export type ObservationKind = 'categorical' | 'mostly_null' | 'constant' | 'duplicates_in_identifier';

export const OBSERVATION_KINDS: readonly ObservationKind[] = ['categorical', 'mostly_null', 'constant', 'duplicates_in_identifier'];

export function isObservationKind(value: unknown): value is ObservationKind {
  return (['categorical', 'mostly_null', 'constant', 'duplicates_in_identifier'] as readonly unknown[]).includes(value);
}

export type GenerationTrigger = 'manual' | 'profile' | 'harvest';

export const GENERATION_TRIGGERS: readonly GenerationTrigger[] = ['manual', 'profile', 'harvest'];

export function isGenerationTrigger(value: unknown): value is GenerationTrigger {
  return (['manual', 'profile', 'harvest'] as readonly unknown[]).includes(value);
}

/**
 * A profile pattern no proposable rule expresses — a categorical column whose values a steward
 * should list (the values are customer data and are never proposed), a mostly-null column the rule
 * DSL has no threshold for. Host-written; the column is one the caller could already see.
 */
export interface ProfileObservation {
  readonly column: string;
  readonly kind: ObservationKind;
  readonly detail: string;
}

export function isProfileObservation(value: unknown): value is ProfileObservation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['column'] === 'string' &&
    (['categorical', 'mostly_null', 'constant', 'duplicates_in_identifier'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['detail'] === 'string'
  );
}

/** The latest generation run for a dataset — possibly automatic, after a profile. */
export interface GenerationRun {
  readonly generated_at: string;
  readonly trigger: GenerationTrigger;
  readonly actor: string;
  readonly columns_considered: number;
  readonly accepted: number;
  readonly by_source: Readonly<Record<string, number>>;
  readonly rejected: Readonly<Record<string, number>>;
  readonly model_status: ModelStatus;
  readonly model_detail: string | null;
  readonly profile_status: ProfileStatus;
  readonly observations: readonly ProfileObservation[];
}

export function isGenerationRun(value: unknown): value is GenerationRun {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['generated_at'] === 'string' &&
    (['manual', 'profile', 'harvest'] as readonly unknown[]).includes(candidate['trigger']) &&
    typeof candidate['actor'] === 'string' &&
    typeof candidate['columns_considered'] === 'number' &&
    typeof candidate['accepted'] === 'number' &&
    candidate['by_source'] !== undefined &&
    candidate['rejected'] !== undefined &&
    (['not_configured', 'no_route', 'unavailable', 'rejected', 'contributed', 'nothing_to_ask', 'deferred'] as readonly unknown[]).includes(candidate['model_status']) &&
    (typeof candidate['model_detail'] === 'string' || candidate['model_detail'] === null) &&
    (['used', 'never_profiled', 'stale', 'empty'] as readonly unknown[]).includes(candidate['profile_status']) &&
    Array.isArray(candidate['observations']) &&
    (candidate['observations'] as readonly unknown[]).every((item) => isProfileObservation(item))
  );
}

/**
 * The rule a recommendation produced. Every progress field is **read from `core.rules` for this
 * response**, never stored with the recommendation; `null` when it could not be read.
 */
export interface AppliedRule {
  readonly id: string;
  readonly version_id: string | null;
  readonly binding_id: string | null;
  /** `draft`, `active` or `deprecated`; `null` when the rule was deleted. */
  readonly state: string | null;
  readonly exists: boolean | null;
  readonly approvals_collected: number | null;
  readonly approvals_required: number | null;
  readonly activation_pending: boolean | null;
}

export function isAppliedRule(value: unknown): value is AppliedRule {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    (typeof candidate['version_id'] === 'string' || candidate['version_id'] === null) &&
    (typeof candidate['binding_id'] === 'string' || candidate['binding_id'] === null) &&
    (typeof candidate['state'] === 'string' || candidate['state'] === null) &&
    (typeof candidate['exists'] === 'boolean' || candidate['exists'] === null) &&
    (typeof candidate['approvals_collected'] === 'number' || candidate['approvals_collected'] === null) &&
    (typeof candidate['approvals_required'] === 'number' || candidate['approvals_required'] === null) &&
    (typeof candidate['activation_pending'] === 'boolean' || candidate['activation_pending'] === null)
  );
}

export interface Suggestion {
  /**
   * The suggestion's own id, which a rule draft cites. **Regenerated on every run**, because a
   * regeneration is a delete-and-insert — so an id names a recommendation as it stood when
   * somebody looked at it, not a durable entity a rule can point at.
   */
  readonly id: string;
  readonly kind: 'not_null' | 'unique' | 'accepted_values' | 'range' | 'freshness' | 'pattern' | 'row_count_anomaly' | 'referential_integrity';
  /**
   * The columns the rule checks. Empty only for `row_count_anomaly`, whose subject is the whole
   * table, so naming a column would misdescribe what it reads.
   */
  readonly columns: readonly string[];
  readonly parameters: Readonly<Record<string, string>>;
  readonly source: CandidateSource;
  /**
   * Why this rule was proposed, in words a steward reads. **Always host-written**, including for a
   * model candidate: carrying a model's own rationale would put unvalidated model text on a
   * screen, which is the leak hard rule 8 exists to prevent.
   */
  readonly rationale: string;
  /**
   * Which validator checks ran. An empty list is refused at construction and by a CHECK constraint
   * — it is what a validator that stopped checking looks like. **Two checks `MODULE_PROMPTS.md`
   * specifies are deliberately absent, and this list says so rather than quietly matching the
   * brief.** *Compiles to valid SQL for this dialect* needs the compiler exposed as a function,
   * which `PostgresRuleExecutor` holds internally. *Dry-run measures the failure rate, reject
   * above 40%* is an **ADR-004 rung 2** operation against a customer's production table, and
   * running one per suggestion would put a scan behind a screen somebody refreshes. A `checks`
   * list that silently matched the specification while omitting two of its items is precisely the
   * shape that makes a validator look stronger than it is.
   */
  readonly checks: readonly string[];
  /**
   * How strongly the evidence supports the rule. **Host-computed from the source and the profile,
   * never reported by a model** — a model cannot certify its own answer.
   */
  readonly confidence: number;
  /**
   * The figures behind it — `rows_profiled`, `nulls_observed`, `distinct_values`. Counts only;
   * nothing a customer's value would fit in.
   */
  readonly evidence: Readonly<Record<string, string>>;
  readonly origin: Origin;
  readonly category: 'completeness' | 'uniqueness' | 'validity' | 'timeliness' | 'consistency' | 'integrity';
  readonly status: SuggestionStatus;
  readonly decided_by: string | null;
  readonly decided_at: string | null;
  readonly dismiss_reason: string | null;
  readonly rule: AppliedRule | null;
  /**
   * Parameters left blank for a person to fill — set only on a **template**, which is how a
   * dimension no evidence covered still gets a recommendation. A template cannot be drafted until
   * every one of these is filled; apply refuses it with a 422 naming them. Never set on a model's
   * recommendation.
   */
  readonly needs_input: readonly string[];
}

export function isSuggestion(value: unknown): value is Suggestion {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    (['not_null', 'unique', 'accepted_values', 'range', 'freshness', 'pattern', 'row_count_anomaly', 'referential_integrity'] as readonly unknown[]).includes(candidate['kind']) &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    candidate['parameters'] !== undefined &&
    (['schema_declared', 'profile_observed', 'schema_inferred', 'model_proposed'] as readonly unknown[]).includes(candidate['source']) &&
    typeof candidate['rationale'] === 'string' &&
    Array.isArray(candidate['checks']) &&
    (candidate['checks'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['confidence'] === 'number' &&
    candidate['evidence'] !== undefined &&
    (['system', 'ai', 'hybrid'] as readonly unknown[]).includes(candidate['origin']) &&
    (['completeness', 'uniqueness', 'validity', 'timeliness', 'consistency', 'integrity'] as readonly unknown[]).includes(candidate['category']) &&
    (['pending', 'drafted', 'bound', 'dismissed'] as readonly unknown[]).includes(candidate['status']) &&
    (typeof candidate['decided_by'] === 'string' || candidate['decided_by'] === null) &&
    (typeof candidate['decided_at'] === 'string' || candidate['decided_at'] === null) &&
    (typeof candidate['dismiss_reason'] === 'string' || candidate['dismiss_reason'] === null) &&
    (candidate['rule'] === null || isAppliedRule(candidate['rule'])) &&
    Array.isArray(candidate['needs_input']) &&
    (candidate['needs_input'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

export interface CoverageGap {
  readonly dimension: 'completeness' | 'uniqueness' | 'validity' | 'timeliness' | 'consistency' | 'integrity' | 'accuracy';
  readonly reason: string;
}

export function isCoverageGap(value: unknown): value is CoverageGap {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['completeness', 'uniqueness', 'validity', 'timeliness', 'consistency', 'integrity', 'accuracy'] as readonly unknown[]).includes(candidate['dimension']) &&
    typeof candidate['reason'] === 'string'
  );
}

export interface GenerationOutcome {
  readonly dataset_id: string;
  /**
   * So a caller can report "6 suggestions from 340 columns" rather than a bare list whose
   * completeness nobody can judge.
   */
  readonly columns_considered: number;
  /**
   * How many suggestions this run stored. **A count, and it used to be the whole list.** This
   * carried `array of Suggestion` — a second copy of what `GET .../suggestions` serves, and a
   * divergent one: the items were projected without the `id` that `Suggestion` requires and that a
   * rule draft cites, because an id is minted when the row is stored and this projection never
   * read it back. So the response violated its own schema, and a client validating items refused
   * the whole outcome. Two shapes for one concept is the defect; the count is what the field was
   * actually used for. A run's *numbers* belong here and its *rows* belong on the listing: those
   * numbers describe a generation, and the list describes the dataset's current state. Sums to
   * `by_source`, which carries the same total broken down by evidence tier.
   */
  readonly accepted: number;
  /**
   * Accepted suggestions per source. This is the module's central claim made measurable: the share
   * needing no model is a number rather than an assertion, and stays answerable after stage 2
   * lands — which is exactly when somebody would otherwise stop being able to tell.
   */
  readonly by_source: Readonly<Record<string, number>>;
  /**
   * Counts per `RejectionReason`. **Counts, never a rejected candidate's detail** — a detail can
   * name a column, and a rejected *model* candidate is the one place a column name may not be one
   * we sent.
   */
  readonly rejected: Readonly<Record<string, number>>;
  /**
   * Numeric columns stage 1 deliberately proposed nothing for, because it has a type and not a
   * distribution. Reported so "we looked and had nothing to say" is distinguishable from "we did
   * not look" — and because it is the list a profile-backed stage 1 would start from.
   */
  readonly unbounded_numeric_columns?: readonly string[];
  /**
   * Whether a validated model candidate came out of this run. **Derived from `model_status`**
   * rather than tracked beside it, so the coarse answer and the precise one cannot disagree — two
   * fields that could is how a screen comes to show "AI suggested" for output no model produced.
   */
  readonly model_consulted: boolean;
  readonly model_status: ModelStatus;
  /**
   * How many raw candidates stage 2 returned, **before** this module's validator judged them. The
   * gap between this and `by_source.model_proposed` is the measurement `core.ai.evals` gates on: a
   * model whose output is mostly refused is one this deployment should not run the feature on.
   */
  readonly model_candidates: number;
  /**
   * Why stage 2 did not run, when it did not. `null` means it did. Named rather than silent: a
   * deployment with no model produces stage-1-only suggestions that look identical to a run where
   * the model contributed nothing, and those are different facts about whether the deployment is
   * working.
   */
  readonly model_unavailable_reason?: string | null;
  readonly profile_status?: ProfileStatus;
  readonly observations?: readonly ProfileObservation[];
  /** Recommendations not re-offered because a person already decided them. */
  readonly already_decided?: number;
  readonly trigger?: GenerationTrigger;
  /**
   * Quality dimensions this dataset has no recommendation for, each with a host-written reason — a
   * table with no date column has nothing a freshness rule can read. Accuracy is always listed: no
   * rule kind measures it. Every other dimension gets a recommendation, completed or as a
   * template, wherever the schema allows one.
   */
  readonly coverage_gaps?: readonly CoverageGap[];
}

export function isGenerationOutcome(value: unknown): value is GenerationOutcome {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['columns_considered'] === 'number' &&
    typeof candidate['accepted'] === 'number' &&
    candidate['by_source'] !== undefined &&
    candidate['rejected'] !== undefined &&
    typeof candidate['model_consulted'] === 'boolean' &&
    (['not_configured', 'no_route', 'unavailable', 'rejected', 'contributed', 'nothing_to_ask', 'deferred'] as readonly unknown[]).includes(candidate['model_status']) &&
    typeof candidate['model_candidates'] === 'number'
  );
}

export interface SuggestionList {
  readonly dataset_id: string;
  readonly suggestions: readonly Suggestion[];
  /** The latest generation run, which may have been automatic. `null` before any run. */
  readonly last_run: GenerationRun | null;
  /** Whether this deployment wired apply, bind and dismiss. */
  readonly can_apply: boolean;
}

export function isSuggestionList(value: unknown): value is SuggestionList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    Array.isArray(candidate['suggestions']) &&
    (candidate['suggestions'] as readonly unknown[]).every((item) => isSuggestion(item)) &&
    (candidate['last_run'] === null || isGenerationRun(candidate['last_run'])) &&
    typeof candidate['can_apply'] === 'boolean'
  );
}

export interface ApplyOutcome {
  readonly suggestion_id: string;
  readonly dataset_id: string;
  readonly status: SuggestionStatus;
  readonly rule_id: string;
  readonly rule_version_id: string;
  readonly activation_request_id: string | null;
  readonly binding_id: string | null;
  /** True when nothing new was created because the step had already happened. */
  readonly replayed: boolean;
  /** What still has to happen, in the platform's own words. */
  readonly then: string;
}

export function isApplyOutcome(value: unknown): value is ApplyOutcome {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['suggestion_id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    (['pending', 'drafted', 'bound', 'dismissed'] as readonly unknown[]).includes(candidate['status']) &&
    typeof candidate['rule_id'] === 'string' &&
    typeof candidate['rule_version_id'] === 'string' &&
    (typeof candidate['activation_request_id'] === 'string' || candidate['activation_request_id'] === null) &&
    (typeof candidate['binding_id'] === 'string' || candidate['binding_id'] === null) &&
    typeof candidate['replayed'] === 'boolean' &&
    typeof candidate['then'] === 'string'
  );
}

export interface DismissOutcome {
  readonly suggestion_id: string;
  readonly status: SuggestionStatus;
}

export function isDismissOutcome(value: unknown): value is DismissOutcome {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['suggestion_id'] === 'string' &&
    (['pending', 'drafted', 'bound', 'dismissed'] as readonly unknown[]).includes(candidate['status'])
  );
}

/**
 * `covers_this` — already bound here and already checks these columns; applying links it.
 * `other_columns` — bound here for other columns; `bound_severities` names the severities a second
 * binding cannot use. `not_here` — not bound to this table.
 */
export type RuleCoverage = 'not_here' | 'covers_this' | 'other_columns';

export const RULE_COVERAGES: readonly RuleCoverage[] = ['not_here', 'covers_this', 'other_columns'];

export function isRuleCoverage(value: unknown): value is RuleCoverage {
  return (['not_here', 'covers_this', 'other_columns'] as readonly unknown[]).includes(value);
}

export interface ExistingRule {
  readonly rule_id: string;
  readonly rule_version_id: string;
  readonly name: string;
  readonly version: string;
  /** Empty means the rule applies to whichever column it is bound to. */
  readonly columns: readonly string[];
  readonly parameters: Readonly<Record<string, string>>;
  readonly description: string | null;
  readonly coverage: RuleCoverage;
  readonly binding_id: string | null;
  readonly bound_severities: readonly string[];
}

export function isExistingRule(value: unknown): value is ExistingRule {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['rule_id'] === 'string' &&
    typeof candidate['rule_version_id'] === 'string' &&
    typeof candidate['name'] === 'string' &&
    typeof candidate['version'] === 'string' &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    candidate['parameters'] !== undefined &&
    (typeof candidate['description'] === 'string' || candidate['description'] === null) &&
    (['not_here', 'covers_this', 'other_columns'] as readonly unknown[]).includes(candidate['coverage']) &&
    (typeof candidate['binding_id'] === 'string' || candidate['binding_id'] === null) &&
    Array.isArray(candidate['bound_severities']) &&
    (candidate['bound_severities'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

export interface MatchingRules {
  readonly suggestion_id: string;
  readonly rules: readonly ExistingRule[];
}

export function isMatchingRules(value: unknown): value is MatchingRules {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['suggestion_id'] === 'string' &&
    Array.isArray(candidate['rules']) &&
    (candidate['rules'] as readonly unknown[]).every((item) => isExistingRule(item))
  );
}

/**
 * **A named schema rather than an inline item, and the generator is why.** An `items:` block
 * written inline emits `Record<string, unknown>` in the TypeScript client, so the envelope guard
 * would check `Array.isArray` and validate nothing about the entries — which is the exact defect
 * S-71 fixed across four screens. A `$ref` gets a real per-item guard, and the badge count is a
 * number a screen renders.
 */
export interface DatasetSuggestionCount {
  readonly dataset_id: string;
  /**
   * How many recommendations are still to review. **Zero means generated, with nothing left to
   * review** — all applied or dismissed, or nothing justified. A dataset never generated is absent
   * from this list, because absence is what "not generated yet" looks like.
   */
  readonly suggestions: number;
}

export function isDatasetSuggestionCount(value: unknown): value is DatasetSuggestionCount {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['suggestions'] === 'number'
  );
}

export interface SuggestionCounts {
  readonly counts: readonly DatasetSuggestionCount[];
}

export function isSuggestionCounts(value: unknown): value is SuggestionCounts {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['counts']) &&
    (candidate['counts'] as readonly unknown[]).every((item) => isDatasetSuggestionCount(item))
  );
}

export interface RuleDraft {
  readonly suggestion_id: string;
  /**
   * The dataset the suggestion turned out to be about — **read from the stored row, never taken
   * from the caller.** A caller-supplied pair could prefill one dataset's column against
   * another's, and the authorization is performed against this value.
   */
  readonly dataset_id: string;
  readonly kind: 'not_null' | 'unique' | 'accepted_values' | 'range' | 'freshness' | 'pattern' | 'row_count_anomaly' | 'referential_integrity';
  /**
   * The columns the rule checks. Empty only for `row_count_anomaly`, whose subject is the whole
   * table, so naming a column would misdescribe what it reads.
   */
  readonly columns: readonly string[];
  readonly parameters: Readonly<Record<string, string>>;
  /**
   * Host-written for every source, including `model_proposed` — a model's own sentence never
   * reaches a stored suggestion, so putting this in a rule's description carries no model text
   * into `rules.rule`.
   */
  readonly rationale: string;
  readonly source: CandidateSource;
  /**
   * A proposed rule name a person edits. Derived rather than left empty, because an empty required
   * field is what makes a prefill feel broken — and deliberately obvious rather than clever, since
   * a name a reviewer keeps by accident is worse than one they can see needs fixing.
   */
  readonly name: string;
  readonly description: string;
  /**
   * Carries `suggested-from:<suggestion_id>`, ready to send straight back on `POST /v1/rules`.
   * That endpoint already accepts tags, so provenance needs no schema change to `core.rules` and
   * no knowledge of suggestions in that module.
   */
  readonly tags: readonly string[];
  /**
   * Always `true`, and on the wire rather than assumed by a client. A prefilled field must read as
   * *proposed* rather than as entered, and a screen deciding that for itself is a screen that can
   * quietly stop doing it.
   */
  readonly requires_review: boolean;
  /**
   * Where the rule is actually created. Served rather than documented, for the reason
   * `accepting_a_suggestion` is: the absence of an accept route here is a governance decision, and
   * a client paraphrasing it would drift from the route list.
   */
  readonly creates_rule_at: string;
  /** What still has to happen afterwards, in the platform's own words. */
  readonly then: string;
}

export function isRuleDraft(value: unknown): value is RuleDraft {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['suggestion_id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    (['not_null', 'unique', 'accepted_values', 'range', 'freshness', 'pattern', 'row_count_anomaly', 'referential_integrity'] as readonly unknown[]).includes(candidate['kind']) &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    candidate['parameters'] !== undefined &&
    typeof candidate['rationale'] === 'string' &&
    (['schema_declared', 'profile_observed', 'schema_inferred', 'model_proposed'] as readonly unknown[]).includes(candidate['source']) &&
    typeof candidate['name'] === 'string' &&
    typeof candidate['description'] === 'string' &&
    Array.isArray(candidate['tags']) &&
    (candidate['tags'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['requires_review'] === 'boolean' &&
    typeof candidate['creates_rule_at'] === 'string' &&
    typeof candidate['then'] === 'string'
  );
}

export interface RulegenCapabilities {
  readonly rung: number;
  readonly proposable_kinds: readonly string[];
  /**
   * Kinds this module refuses to propose, each with its reason. `custom_sql` is the one that
   * matters and it is a security boundary: a suggestion carrying arbitrary SQL is one click from
   * running against a customer's production database, and no validator makes that safe — checking
   * it parses says nothing about what it does.
   */
  readonly never_proposed: Readonly<Record<string, string>>;
  readonly sources: readonly CandidateSource[];
  readonly checks: readonly string[];
  readonly rejection_reasons: readonly RejectionReason[];
  /**
   * **The bound is the tenant, not the individual user, and this field states that.** The column
   * list a candidate is judged against is read on a session bound to the caller's tenant under
   * row-level security; the platform has no per-column grant model, so two callers in one tenant
   * receive the same list. `MODULE_PROMPTS.md` asks for "a column the requesting user cannot see",
   * which is finer than the platform can currently express. What is enforced is real — a candidate
   * naming another tenant's column, or one not on this dataset, is refused — and the gap is named
   * here rather than implied, because a published statement about a security control that
   * overstates it is worse than one that omits it.
   */
  readonly authorization_policy: string;
  /**
   * What applying a recommendation does, in the platform's own words: a draft rule through
   * `core.rules`, then two-eyes activation, then binding. Served so a client cannot paraphrase a
   * governance step into something weaker.
   */
  readonly accepting_a_suggestion: string;
  /** Per kind, the parameters a person may set when applying. */
  readonly editable_parameters: Readonly<Record<string, readonly string[]>>;
  /** The severities a binding may carry — `core.rules`' own vocabulary. */
  readonly severities: readonly string[];
}

export function isRulegenCapabilities(value: unknown): value is RulegenCapabilities {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['rung'] === 'number' &&
    Array.isArray(candidate['proposable_kinds']) &&
    (candidate['proposable_kinds'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    candidate['never_proposed'] !== undefined &&
    Array.isArray(candidate['sources']) &&
    (candidate['sources'] as readonly unknown[]).every((item) => isCandidateSource(item)) &&
    Array.isArray(candidate['checks']) &&
    (candidate['checks'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['rejection_reasons']) &&
    (candidate['rejection_reasons'] as readonly unknown[]).every((item) => isRejectionReason(item)) &&
    typeof candidate['authorization_policy'] === 'string' &&
    typeof candidate['accepting_a_suggestion'] === 'string' &&
    candidate['editable_parameters'] !== undefined &&
    Array.isArray(candidate['severities']) &&
    (candidate['severities'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

/** What putting a recommendation in place would take. Only `bind` is selectable. */
export type RecommendedAction = 'bind' | 'already_bound' | 'needs_new_rule' | 'awaiting_approval';

export const RECOMMENDED_ACTIONS: readonly RecommendedAction[] = ['bind', 'already_bound', 'needs_new_rule', 'awaiting_approval'];

export function isRecommendedAction(value: unknown): value is RecommendedAction {
  return (['bind', 'already_bound', 'needs_new_rule', 'awaiting_approval'] as readonly unknown[]).includes(value);
}

export interface AutoSuggestRecommendation {
  readonly suggestion_id: string;
  readonly kind: string;
  readonly category: string;
  readonly columns: readonly string[];
  readonly parameters: Readonly<Record<string, string>>;
  /** The recommendation's rationale. Host-written for every source. */
  readonly description: string;
  readonly source: CandidateSource;
  readonly origin: Origin;
  readonly confidence: number;
  readonly action: RecommendedAction;
  /** Ticked by default. Only ever true on a `bind` row backed by strong evidence. */
  readonly preselected: boolean;
  /** Why it is not preselected, or what is in the way. Host-written. */
  readonly note: string | null;
  /** The approved rule that would be bound, or the rule drafted from it. */
  readonly rule_id: string | null;
  /** The approved library rule's name, when one would be bound. */
  readonly rule_name: string | null;
  /** The name a new rule drafted from this recommendation gets by default. */
  readonly proposed_name: string;
  /**
   * Parameters left blank for a person to fill — set only on a **template**, which is how a
   * dimension no evidence covered still gets a recommendation. A template cannot be drafted until
   * every one of these is filled; apply refuses it with a 422 naming them. Never set on a model's
   * recommendation.
   */
  readonly needs_input: readonly string[];
}

export function isAutoSuggestRecommendation(value: unknown): value is AutoSuggestRecommendation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['suggestion_id'] === 'string' &&
    typeof candidate['kind'] === 'string' &&
    typeof candidate['category'] === 'string' &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    candidate['parameters'] !== undefined &&
    typeof candidate['description'] === 'string' &&
    (['schema_declared', 'profile_observed', 'schema_inferred', 'model_proposed'] as readonly unknown[]).includes(candidate['source']) &&
    (['system', 'ai', 'hybrid'] as readonly unknown[]).includes(candidate['origin']) &&
    typeof candidate['confidence'] === 'number' &&
    (['bind', 'already_bound', 'needs_new_rule', 'awaiting_approval'] as readonly unknown[]).includes(candidate['action']) &&
    typeof candidate['preselected'] === 'boolean' &&
    (typeof candidate['note'] === 'string' || candidate['note'] === null) &&
    (typeof candidate['rule_id'] === 'string' || candidate['rule_id'] === null) &&
    (typeof candidate['rule_name'] === 'string' || candidate['rule_name'] === null) &&
    typeof candidate['proposed_name'] === 'string' &&
    Array.isArray(candidate['needs_input']) &&
    (candidate['needs_input'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

export interface AutoSuggestPreview {
  readonly dataset_id: string;
  readonly generation: GenerationOutcome;
  readonly recommendations: readonly AutoSuggestRecommendation[];
  /** Recommendations left out because a person dismissed them. */
  readonly dismissed: number;
}

export function isAutoSuggestPreview(value: unknown): value is AutoSuggestPreview {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    candidate['generation'] !== undefined &&
    Array.isArray(candidate['recommendations']) &&
    (candidate['recommendations'] as readonly unknown[]).every((item) => isAutoSuggestRecommendation(item)) &&
    typeof candidate['dismissed'] === 'number'
  );
}

/**
 * What binding one selection did. Only `bound` created a binding, and only ever of a rule two
 * people had already approved.
 */
export type AutoSuggestOutcome = 'bound' | 'already_bound' | 'not_bindable' | 'failed';

export const AUTO_SUGGEST_OUTCOMES: readonly AutoSuggestOutcome[] = ['bound', 'already_bound', 'not_bindable', 'failed'];

export function isAutoSuggestOutcome(value: unknown): value is AutoSuggestOutcome {
  return (['bound', 'already_bound', 'not_bindable', 'failed'] as readonly unknown[]).includes(value);
}

export interface AutoSuggestItem {
  readonly suggestion_id: string;
  /** Null only for an id that no longer resolves in this dataset. */
  readonly kind: string | null;
  readonly columns: readonly string[];
  readonly outcome: AutoSuggestOutcome;
  /** Why this outcome, host-written. Never model text and never a data value. */
  readonly detail: string;
  readonly rule_id: string | null;
  readonly binding_id: string | null;
}

export function isAutoSuggestItem(value: unknown): value is AutoSuggestItem {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['suggestion_id'] === 'string' &&
    (typeof candidate['kind'] === 'string' || candidate['kind'] === null) &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    (['bound', 'already_bound', 'not_bindable', 'failed'] as readonly unknown[]).includes(candidate['outcome']) &&
    typeof candidate['detail'] === 'string' &&
    (typeof candidate['rule_id'] === 'string' || candidate['rule_id'] === null) &&
    (typeof candidate['binding_id'] === 'string' || candidate['binding_id'] === null)
  );
}

export interface AutoSuggestSummary {
  readonly selected: number;
  readonly bound: number;
  readonly already_bound: number;
  readonly not_bindable: number;
  readonly failed: number;
}

export function isAutoSuggestSummary(value: unknown): value is AutoSuggestSummary {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['selected'] === 'number' &&
    typeof candidate['bound'] === 'number' &&
    typeof candidate['already_bound'] === 'number' &&
    typeof candidate['not_bindable'] === 'number' &&
    typeof candidate['failed'] === 'number'
  );
}

export interface AutoSuggestResult {
  readonly dataset_id: string;
  /** The severity every binding this call created was given. */
  readonly severity: string;
  readonly summary: AutoSuggestSummary;
  readonly results: readonly AutoSuggestItem[];
}

export function isAutoSuggestResult(value: unknown): value is AutoSuggestResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['severity'] === 'string' &&
    candidate['summary'] !== undefined &&
    Array.isArray(candidate['results']) &&
    (candidate['results'] as readonly unknown[]).every((item) => isAutoSuggestItem(item))
  );
}
